<template>
  <div class="branding-page-container" :style="customStyle">
    <CompanyDetail
      v-rect-loading="loading"
      class="company-info"
      :class="loading && 'is-loading'"
      :isMSDCustom="isMSDCustom"
      :footerData="pageInfo"
      :pageData="pageInfo?.[dataKey]"
      :activePage="activePage"
      :companyBaseInfo="companyBaseInfo"
      :showChatBot="showChatbot"
      :personalAgreementStatus="personalAgreementStatus"
      :surveyInfo="surveyInfo"
      @videoNavigationShowChange="onVideoNavigationShowChange"
    />
    <Chatbot
      v-if="showChatbot"
      v-show="!hiddenChatBot"
      class="chatbot"
      :useAiChatbot="useAiChatbot"
      :generateUserId="''"
      :showChatbotMessageBox="false"
      :company="companyBaseInfo.company"
    />
  </div>
</template>

<script>
import CompanyDetail from './components/companyDetail.vue'
import Chatbot from '@/components/job/chatbot/index.vue'
import { mapActions, mapGetters } from 'vuex'
import { COMPANY_PAGE_TYPE } from '@/constant/company'
import { deviceType } from '@/utils/common'
import configWxShare, { configWeComShare } from '@/utils/wxShareConfig'
import { addI18nProperty, isEn } from '@/i18n'
import _ from 'lodash'
import { msdPcJobListRouteMap } from '../../msdCustomUrl/pc/route'
import { msdMobileJobListRouteMap } from '../../msdCustomUrl/mobile/route'

export default {
  components: {
    CompanyDetail,
    Chatbot
  },
  props: {
    isMSDCustom: {
      type: Boolean,
      default: false
    },
    customActivePage: {
      type: String,
      default: ''
    },
    customCompanyId: {
      required: false
    }
  },
  metaInfo () {
    return {
      link: this.metaInfoLinkList
    }
  },
  data () {
    return {
      pageInfo: null,
      loading: false,
      companyBaseInfo: null,
      metaInfoLinkList: [],
      customStyle: null,
      wxShareInfo: null,
      hiddenChatBot: false,
      weComAppInfo: null,
      personalAgreementStatus: null,
      surveyInfo: {}
    }
  },
  computed: {
    ...mapGetters('account', ['isCandidateAccount']),
    msdJobListRouteMap () {
      return deviceType.isMobile ? msdMobileJobListRouteMap : msdPcJobListRouteMap
    },
    companyId () {
      return this.customCompanyId || this.$route.params.companyId
    },
    activePage () {
      return this.$route.params.page || this.customActivePage
    },
    dataKey () {
      return this.activePage === COMPANY_PAGE_TYPE.BRANDING ? 'branding_page' : 'landing_page'
    },
    hasLogin () {
      const account = this.getCurrentAccount()
      return Boolean(account?.id)
    },
    showChatbot () {
      return Boolean(this.companyBaseInfo?.show_chatbot || this.companyBaseInfo?.show_ai_chatbot)
    },
    useAiChatbot () {
      return Boolean(this.companyBaseInfo?.show_ai_chatbot)
    }
  },
  beforeRouteEnter (to, from, next) {
    const companyId = to.params.companyId
    const msdJobListRouteMap = deviceType.isMobile ? msdMobileJobListRouteMap : msdPcJobListRouteMap
    const isMsdCustomCompany = _.includes(_.keys(msdJobListRouteMap), `${companyId}`)
    if (isMsdCustomCompany) {
      let path = 'professionals'
      if (_.includes(to.path, '/about') || _.includes(to.path, '/branding')) {
        path = 'about'
      } else if (_.includes(to.path, '/careers') || _.includes(to.path, '/landing')) {
        path = 'careers'
      }
      next({ path: `/msdchina/${path}`, query: to.query })
    }
    next()
  },
  created () {
    if (deviceType.isPc) {
      const isMsdCustomCompany = _.includes(_.keys(this.msdJobListRouteMap), `${this.companyId}`)
      if (this.isMSDCustom && isMsdCustomCompany) {
        let path = 'professionals'
        if (_.includes(this.$route.path, '/about')) {
          path = 'about'
        } else if (_.includes(this.$route.path, '/careers')) {
          path = 'careers'
        }
        this.$router.replace({ path: `/msdchina/${path}`, query: this.$route.query })
      } else {
        this.$router.replace({ path: `/recruiting/company-detail/${this.companyId}/`, query: this.$route.query })
      }
      return
    }
    this.refreshPageData()
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('company', ['getCompanyBrandingPageInfo', 'getCompanyBaseInfo']),
    ...mapActions('personal', ['getAgreementStatus']),
    ...mapActions('survey', ['getSurveyInfo']),
    async refreshPageData () {
      this.loading = true
      try {
        await Promise.all([
          this.getCompanyInfo(),
          this.getCompanyBrandingInfo(),
          this.initPersonalAgreementStatus(),
          this.initSurveyInfo()
        ])
      } finally {
        this.loading = false
      }
    },
    // 获取公司主页信息，包括主页（员工）和信息页（员工）
    async getCompanyBrandingInfo () {
      const params = {
        company_id: this.companyId
      }
      const resp = await this.getCompanyBrandingPageInfo(params)
      if (!resp) {
        return
      }

      // 没有配置公司主页则跳转到职位列表页
      if (!resp.branding_page) {
        if (this.isMSDCustom) {
          const route = _.get(this.msdJobListRouteMap, this.companyId)
          this.$router.replace(route.path)
        } else {
          this.$router.replace(`/mobile-web-more-jobs/${this.companyId}`)
        }
        return
      }

      if (resp.branding_page?.webpage_items) {
        resp.branding_page.webpageItems = JSON.parse(resp.branding_page.webpage_items)
        resp.branding_page.showGap = resp.branding_page.show_gap
      }
      if (resp.landing_page?.webpage_items) {
        resp.landing_page.webpageItems = JSON.parse(resp.landing_page.webpage_items)
        resp.landing_page.showGap = resp.landing_page.show_gap
      }
      const wechatShareInfo = resp?.[this.dataKey]?.wx_share_info
      this.initWechatShare(wechatShareInfo)
      const brandingPage = resp.branding_page
      const landingPage = resp.landing_page
      if (brandingPage) {
        addI18nProperty(brandingPage, 'i18nName', 'menu_cn_name', 'menu_en_name')
      }
      if (landingPage) {
        addI18nProperty(landingPage, 'i18nName', 'menu_cn_name', 'menu_en_name')
      }
      this.pageInfo = resp
    },
    async getCompanyInfo () {
      const resp = await this.getCompanyBaseInfo({ id: this.companyId })
      if (!resp) {
        return
      }
      if (resp.show_chatbot) {
        this.initChatbotStyle()
      }
      if (resp.has_color_customization) {
        this.customStyle = {
          '--custom-color': resp.custom_color
        }
      }
      this.companyBaseInfo = resp
      this.weComAppInfo = resp.wecom_app_info
    },
    async initPersonalAgreementStatus () {
      if (!this.hasLogin || !this.isCandidateAccount) {
        return
      }
      const resp = await this.getAgreementStatus({
        company_id: this.companyId
      })
      if (resp) {
        this.personalAgreementStatus = resp
      }
    },
    async initSurveyInfo () {
      if (!this.hasLogin || !this.isCandidateAccount) {
        return
      }
      const resp = await this.getSurveyInfo(this.companyId)
      if (resp) {
        this.surveyInfo = resp
      }
    },
    initChatbotStyle () {
      let baseUrl = process.env.VUE_APP_STYLE_BASE_URL
      if (baseUrl.endsWith('/')) {
        baseUrl = baseUrl.substring(0, baseUrl.length - 1)
      }
      this.metaInfoLinkList.push({
        rel: 'stylesheet',
        href: `${baseUrl}/static/style/henkel-bot.css?_t=${Date.now()}`
      })
    },
    async initWechatShare (wechatShareInfo) {
      const wxShareInfo = wechatShareInfo && JSON.parse(wechatShareInfo)
      wxShareInfo.link = wxShareInfo.normal_share_url || window.location.href
      this.wxShareInfo = wxShareInfo
      if (this.wxShareInfo?.is_activated) {
        this.$eventBus.$emit('setCustomTitle', this.wxShareInfo.moment_title)
      } else {
        this.$eventBus.$emit('setCustomTitle', this.$t('company.label.title'))
      }
      if (deviceType.isWeChatWork) {
        wxShareInfo.image_url = window.location.origin + wxShareInfo.logo
        configWeComShare(this.weComAppInfo, wxShareInfo)
      } else {
        const company = this.companyBaseInfo.company
        const rootCompanyId = company.root_company?.id
        const companyId = company.id
        configWxShare(this, null, rootCompanyId, companyId)
      }
    },
    onVideoNavigationShowChange (value) {
      this.hiddenChatBot = value
    }
  },
  watch: {
    activePage () {
      if (!this.pageInfo) {
        return
      }
      const wechatShareInfo = this.pageInfo[this.dataKey]?.wx_share_info
      this.initWechatShare(wechatShareInfo)
    },
    dataKey () {
      if (!this.pageInfo) {
        return
      }
      const pageData = this.pageInfo[this.dataKey]
      this.pageInfo[this.dataKey] = null
      this.$nextTick(() => {
        this.pageInfo[this.dataKey] = pageData
      })
    },
    companyId () {
      this.refreshPageData()
    },
    '$i18n.locale' () {
      const language = isEn() ? 'en' : 'zh_cn'
      if (this.data?.branding_page?.language_code === language) {
        return
      }
      this.getCompanyBrandingInfo()
    }
  }
}
</script>
<style lang="scss" scoped>
.branding-page-container {
  height: 100vh;
  height: 100dvh;

  .chatbot {
    ::v-deep #btnDialogToggle {
      width: 60px;
      height: 60px;
    }
  }

  .company-info {
    &.is-loading {
      height: 100vh;
      height: 100dvh;
      overflow: hidden;
    }
  }
}
</style>
